import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { SubtleLink } from '@liveauctioneers/hammer-ui-core/link';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';

import CatalogDate from '@/components/CatalogDate/CatalogDate';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { useAppSelector } from '@/redux/hooks';
import {
    getItemSummaryIsPassed,
    getItemSummaryIsPaused,
    getItemSummaryIsSold,
    getItemSummaryLotEndTimeEstimatedTs,
    getItemSummarySaleStartTs,
} from '@/redux/modules/item/summary/itemSummary.selectors';

import { useGetTimedPlusCountdown } from './hooks/useGetTimedPlusCountdown';
import { useRefetchItemSummariesIfNeeded } from './hooks/useRefetchItemSummariesIfNeeded';

export type ItemCardCountdownProps = {
    className?: string;
    hasNewSimilarItemsIconCTA?: boolean;
    itemId: number;
    onNavigate?: () => void;
};

export function ItemCardCountdown({
    className,
    hasNewSimilarItemsIconCTA = false,
    itemId,
    onNavigate,
}: ItemCardCountdownProps) {
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isPaused = useAppSelector((state) => getItemSummaryIsPaused(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const lotEndTimeEstimatedTs = useAppSelector((state) => getItemSummaryLotEndTimeEstimatedTs(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummarySaleStartTs(state, itemId));

    const { hoursUntilEnd, minutesUntilEnd, saleEnd, saleStartIsPassed, secondsUntilEnd, today, totalDaysFromEnd } =
        useGetTimedPlusCountdown({ itemId });

    useRefetchItemSummariesIfNeeded({ itemId, saleEnd, today });
    const itemHref = useLocalizedPath(`/item/${itemId}`);

    if (isPaused) {
        return null;
    }

    if (isSold || isPassed || saleEnd < today) {
        return (
            <BodySecondary className={className}>
                <CatalogDate
                    className={className}
                    date={lotEndTimeEstimatedTs}
                    hasNewSimilarItemsIconCTA={hasNewSimilarItemsIconCTA}
                    showOnlyYear
                />
            </BodySecondary>
        );
    }

    // IF AFTER SALE START AND NOT SOLD
    if (saleStartIsPassed) {
        // MORE THAN A DAY AWAY OR ONE DAY AWAY FROM SALE END
        if (totalDaysFromEnd >= 1) {
            return (
                <SubtleLink
                    className="shrink grow"
                    href={itemHref}
                    onClick={onNavigate}
                >
                    <BodySecondary
                        className={cn('flex items-center', className)}
                        color="critical"
                        role="button"
                    >
                        <FormattedMessage
                            id="itemBidding.endsInDays"
                            values={{
                                days: totalDaysFromEnd,
                                hours: hoursUntilEnd,
                            }}
                        />
                    </BodySecondary>
                </SubtleLink>
            );
        }

        // LESS THAN A DAY AWAY FROM SALE END
        if (totalDaysFromEnd === 0 && hoursUntilEnd !== 0) {
            return (
                <SubtleLink
                    className="shrink grow"
                    href={itemHref}
                    onClick={onNavigate}
                >
                    <BodySecondary
                        className={cn('flex items-center', className)}
                        color="critical"
                        role="button"
                    >
                        <FormattedMessage
                            id="itemBidding.endsInTime"
                            values={{
                                hours: hoursUntilEnd,
                                minutes: minutesUntilEnd,
                            }}
                        />
                    </BodySecondary>
                </SubtleLink>
            );
        }

        // LESS THAN ONE MINUTE AWAY FROM SALE END
        if (totalDaysFromEnd === 0 && hoursUntilEnd === 0 && minutesUntilEnd === 0) {
            return (
                <SubtleLink
                    className="shrink grow"
                    href={itemHref}
                    onClick={onNavigate}
                >
                    <BodySecondary
                        className={cn('flex items-center', className)}
                        color="critical"
                        role="button"
                    >
                        <FormattedMessage
                            id="itemBidding.endsInTimeSeconds"
                            values={{ seconds: secondsUntilEnd }}
                        />
                    </BodySecondary>
                </SubtleLink>
            );
        }

        // LESS THAN ONE HOUR AWAY FROM SALE END
        if (totalDaysFromEnd === 0 && hoursUntilEnd === 0) {
            return (
                <SubtleLink
                    className="shrink grow"
                    href={itemHref}
                    onClick={onNavigate}
                >
                    <BodySecondary
                        className={cn('flex items-center', className)}
                        color="critical"
                        role="button"
                    >
                        <FormattedMessage
                            id="itemBidding.endsInTimeMinutes"
                            values={{
                                minutes: minutesUntilEnd,
                                seconds: secondsUntilEnd,
                            }}
                        />
                    </BodySecondary>
                </SubtleLink>
            );
        }

        // ENDS IN MORE THAN ONE HOUR
        if (totalDaysFromEnd < 0) {
            return (
                <SubtleLink
                    className="shrink grow"
                    href={itemHref}
                    onClick={onNavigate}
                >
                    <BodySecondary
                        className={cn('flex items-center', className)}
                        color="critical"
                        role="button"
                    >
                        <FormattedMessage
                            id="itemBidding.endsIn"
                            values={{
                                hours: hoursUntilEnd,
                                minutes: minutesUntilEnd,
                            }}
                        />
                    </BodySecondary>
                </SubtleLink>
            );
        }
    }

    return (
        <BodySecondary className={className}>
            <CatalogDate
                className={className}
                date={saleStartTs}
                hasNewSimilarItemsIconCTA={hasNewSimilarItemsIconCTA}
                showDateWithoutYear
                timedPlus
            />
        </BodySecondary>
    );
}
